// src/components/modals/ConfirmDeleteModal.jsx
import { X, AlertTriangle, User, Calendar } from 'lucide-react'
import { format, parseISO } from 'date-fns'

const ConfirmDeleteModal = ({ isOpen, onClose, onConfirm, loading, attendance }) => {
  if (!isOpen || !attendance) return null

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'PPP')
    } catch {
      return dateString
    }
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      present: { bg: 'bg-green-100', text: 'text-green-800', label: 'Present' },
      absent: { bg: 'bg-red-100', text: 'text-red-800', label: 'Absent' },
      on_leave: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'On Leave' }
    }

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status || 'Unknown' }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    )
  }

  const handleConfirm = () => {
    onConfirm(attendance._id)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Delete Attendance</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Warning Message */}
          <div className="mb-6">
            <p className="text-gray-600">
              Are you sure you want to delete this attendance record? This action cannot be undone.
            </p>
          </div>

          {/* Attendance Details */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-6">
            <div className="flex items-center space-x-3">
              <User className="h-4 w-4 text-gray-400" />
              <div>
                <span className="text-sm font-medium text-gray-900">Employee: </span>
                <span className="text-sm text-gray-600">{attendance.employee?.fullName || 'Unknown Employee'}</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Calendar className="h-4 w-4 text-gray-400" />
              <div>
                <span className="text-sm font-medium text-gray-900">Date: </span>
                <span className="text-sm text-gray-600">{formatDate(attendance.date)}</span>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="w-4 h-4" /> {/* Spacer */}
              <div>
                <span className="text-sm font-medium text-gray-900">Status: </span>
                {getStatusBadge(attendance.status)}
              </div>
            </div>
          </div>

          {/* Delete Restriction Notice */}
          {attendance.status !== 'absent' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">Deletion Restricted</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Only attendance records with "Absent" status can be deleted. 
                    This record has a status of "{attendance.status === 'on_leave' ? 'On Leave' : 'Present'}" and cannot be deleted.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading || attendance.status !== 'absent'}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Deleting...' : 'Delete Record'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConfirmDeleteModal